import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../utils/api";
import { ApiResponse } from "../types/api";

export interface WeatherPreset {
  id: number;
  name: string;
  preset_type: "date" | "custom";
  params: Record<string, any>;
  created_at: string;
}

export const useWeatherPresets = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["weatherPresets", projectId],
    queryFn: () =>
      api.request<ApiResponse<WeatherPreset[]>>(
        `/api/projects/${projectId}/weather-presets/`
      ),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useCreateWeatherPreset = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      preset_type: "date" | "custom";
      params: Record<string, any>;
    }) => {
      const res = await api.request<ApiResponse<WeatherPreset>>(
        `/api/projects/${projectId}/weather-presets/`,
        { method: "POST", body: payload }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weatherPresets", projectId] });
    },
  });
};

export const useDeleteWeatherPreset = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (presetId: number) => {
      await api.request<void>(
        `/api/projects/${projectId}/weather-presets/${presetId}`,
        { method: "DELETE" }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weatherPresets", projectId] });
    },
  });
};
